import { withBackend } from '@/services/api/backend';
import { simulate } from '@/services/api/client';

export type RecurringPlanId = 'starter' | 'pro' | 'business';

export interface CreateSubscriptionResult {
  subscriptionId: string;
  status: string;
  /** First-invoice PaymentIntent secret to confirm client-side (SCA). null when already paid. */
  clientSecret: string | null;
  /** The company created (or reused, on a re-subscribe) for this subscription. */
  companyId: string;
}

export interface SubscriptionBillingAddress {
  street?: string;
  city: string;
  state?: string;
  postalCode?: string;
  country: string;
}

export interface ActiveRecurringSubscription {
  id: string;
  planId: string;
  planName: string;
  status: string;
  price: number;
  discountedPrice: number;
  discountPercentage: number;
  billingInterval: 'monthly' | 'yearly';
  billingPeriod: 'month' | 'year';
  currency: string;
  stripeSubscriptionId: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  currentPeriodStart: string | null;
}

export interface SubscriptionInvoice {
  id: string;
  subscriptionId: string | null;
  amount: number;
  discountApplied: number;
  status: string;
  paymentMethod: string;
  createdAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

/** Map an industry plan (id or display name) to a self-serve recurring plan id. */
export function normalizePlan(planIdOrName?: string): RecurringPlanId | null {
  const s = (planIdOrName ?? '').toLowerCase();
  if (s.includes('starter')) return 'starter';
  if (s.includes('pro') || s.includes('growth')) return 'pro';
  if (s.includes('business')) return 'business';
  return null; // enterprise / free trial / unknown → not self-serve recurring
}

/**
 * Real Stripe recurring subscriptions (industry plans). Mock-first via
 * withBackend — falls back to a local stub when the backend is off, so the
 * checkout demo still completes.
 */
export const recurringSubscriptionService = {
  create: (input: {
    industryId: string;
    industryName?: string;
    planId: RecurringPlanId;
    paymentMethodId: string;
    companyName: string;
    address: SubscriptionBillingAddress;
    billingInterval: 'monthly' | 'yearly';
  }) =>
    withBackend<CreateSubscriptionResult>(
      'createBusinessSubscription',
      input,
      () => simulate({ subscriptionId: `sub_${Date.now().toString(36)}`, status: 'active', clientSecret: null, companyId: `co_${Date.now().toString(36)}` }, 600),
    ),

  cancel: (subscriptionId: string, immediately = false) =>
    withBackend<{ status: string }>(
      'cancelBusinessSubscriptionRecurring',
      { subscriptionId, immediately },
      () => simulate({ status: immediately ? 'canceled' : 'active' }, 400),
    ),

  update: (subscriptionId: string, newPlanId: RecurringPlanId) =>
    withBackend<{ status: string }>(
      'updateBusinessSubscriptionRecurring',
      { subscriptionId, newPlanId },
      () => simulate({ status: 'active' }, 400),
    ),

  updatePaymentMethod: (subscriptionId: string, paymentMethodId: string) =>
    withBackend<{ status: string; last4: string | null; brand: string | null }>(
      'updateSubscriptionPaymentMethod',
      { subscriptionId, paymentMethodId },
      () => simulate({ status: 'active', last4: paymentMethodId.slice(-4), brand: 'visa' }, 500),
    ),

  getActive: () =>
    withBackend<{ subscription: ActiveRecurringSubscription | null }>(
      'getActiveBusinessSubscription',
      {},
      () => simulate({ subscription: null }, 300),
    ).then((r) => r.subscription),

  getInvoices: (limit = 24) =>
    withBackend<{ invoices: SubscriptionInvoice[] }>(
      'listSubscriptionInvoices',
      { limit },
      () => simulate({ invoices: [] }, 300),
    ).then((r) => r.invoices),
};
